import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle,
  Circle,
  Loader2,
  Bot,
  MessageSquare,
  Zap,
  HelpCircle,
  TestTube,
  Rocket,
  ChevronRight,
  RefreshCw,
  Mail,
  X,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import {
  fetchClaw9Accounts,
  fetchBotAgents,
  fetchAccountFAQs,
} from "../api/claw9";

// Onboarding steps configuration
const ONBOARDING_STEPS = [
  {
    id: "signup",
    label: "Create Account",
    labelVi: "Tạo tài khoản",
    icon: CheckCircle,
    description: "Your account is ready!",
    descriptionVi: "Tài khoản đã sẵn sàng!",
  },
  {
    id: "create_bot",
    label: "Create Your Bot",
    labelVi: "Tạo Bot",
    icon: Bot,
    description: "Set up your AI assistant",
    descriptionVi: "Thiết lập trợ lý AI của bạn",
  },
  {
    id: "connect_zalo",
    label: "Connect Zalo",
    labelVi: "Kết nối Zalo",
    icon: MessageSquare,
    description: "Scan QR code to connect",
    descriptionVi: "Quét mã QR để kết nối",
  },
  {
    id: "add_faqs",
    label: "Add FAQs",
    labelVi: "Thêm câu hỏi",
    icon: HelpCircle,
    description: "Train your bot with Q&A",
    descriptionVi: "Huấn luyện bot với câu hỏi",
  },
  {
    id: "test_bot",
    label: "Test Your Bot",
    labelVi: "Thử nghiệm",
    icon: TestTube,
    description: "Send a test message",
    descriptionVi: "Gửi tin nhắn thử nghiệm",
  },
  {
    id: "complete",
    label: "Go Live!",
    labelVi: "Hoàn thành!",
    icon: Rocket,
    description: "Your bot is ready",
    descriptionVi: "Bot đã sẵn sàng",
  },
];

// Step indicator component
const StepIndicator = ({ step, index, currentStepIndex, completedSteps, language = "en" }) => {
  const isCompleted = completedSteps.includes(step.id);
  const isCurrent = index === currentStepIndex;
  const isPending = index > currentStepIndex;
  const Icon = step.icon;

  return (
    <div className="flex items-center">
      <div className="flex flex-col items-center">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            isCompleted
              ? "bg-emerald-500 text-white"
              : isCurrent
              ? "bg-[#ff4d4d] text-white ring-4 ring-[#ff4d4d]/20"
              : "bg-white/10 text-white/40"
          }`}
        >
          {isCompleted ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <Icon className="w-5 h-5" />
          )}
        </div>
        <span
          className={`mt-2 text-xs font-medium ${
            isCompleted || isCurrent ? "text-white" : "text-white/40"
          }`}
        >
          {language === "vi" ? step.labelVi : step.label}
        </span>
      </div>
      {index < ONBOARDING_STEPS.length - 1 && (
        <div
          className={`w-12 h-0.5 mx-2 mt-[-20px] ${
            isCompleted ? "bg-emerald-500" : "bg-white/10"
          }`}
        />
      )}
    </div>
  );
};

// QR Code connection component
const ZaloQRConnect = ({ botId, onConnected, language = "en" }) => {
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState("loading"); // loading, waiting, connected, expired, error
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [emailSent, setEmailSent] = useState(false);

  const generateQR = useCallback(async () => {
    setStatus("loading");
    setError(null);

    try {
      const response = await fetch(`/api/claw9/bots/${botId}/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to generate QR code");
      }

      const data = await response.json();
      setQrCode(data.qrCode || data.qr_code);
      setStatus("waiting");
      setPollCount(0);
    } catch (err) {
      console.error("[Onboarding] QR generation error:", err);
      setError(err.message);
      setStatus("error");
    }
  }, [botId]);

  // Initial QR generation
  useEffect(() => {
    if (botId) {
      generateQR();
    }
  }, [botId, generateQR]);

  // Poll for auth status
  useEffect(() => {
    if (status !== "waiting") return;

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/claw9/bots/${botId}/auth/status`);
        const data = await response.json();

        if (data.status === "connected" || data.authenticated) {
          setStatus("connected");
          clearInterval(interval);
          if (onConnected) onConnected(data);
        } else if (data.qrExpired || data.expired) {
          setStatus("expired");
          clearInterval(interval);
        }

        setPollCount((c) => c + 1);

        // Auto-expire after 60 polls (3 minutes)
        if (pollCount > 60) {
          setStatus("expired");
          clearInterval(interval);
        }
      } catch (err) {
        console.warn("[Onboarding] Poll error:", err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [status, botId, pollCount, onConnected]);

  const sendQRToEmail = async () => {
    try {
      await fetch(`/api/claw9/bots/${botId}/auth/email`, {
        method: "POST",
      });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (err) {
      console.error("[Onboarding] Email error:", err);
    }
  };

  const texts = {
    en: {
      scanning: "Waiting for QR scan...",
      instructions: "Open Zalo app → Settings → Scan QR Code",
      sendEmail: "Send QR to Email",
      emailSent: "QR sent to your email!",
      expired: "QR code expired",
      regenerate: "Generate New QR",
      connected: "Zalo Connected!",
      continue: "Continue",
      error: "Failed to load QR code",
      retry: "Try Again",
    },
    vi: {
      scanning: "Đang chờ quét mã QR...",
      instructions: "Mở Zalo → Cài đặt → Quét mã QR",
      sendEmail: "Gửi QR qua Email",
      emailSent: "Đã gửi QR qua email!",
      expired: "Mã QR đã hết hạn",
      regenerate: "Tạo mã QR mới",
      connected: "Đã kết nối Zalo!",
      continue: "Tiếp tục",
      error: "Không thể tải mã QR",
      retry: "Thử lại",
    },
  };
  const t = texts[language] || texts.en;

  return (
    <div className="flex flex-col items-center py-6">
      {status === "loading" && (
        <div className="w-48 h-48 bg-white/5 rounded-xl flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
        </div>
      )}

      {status === "waiting" && qrCode && (
        <>
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <img
              src={qrCode}
              alt="Zalo QR Code"
              className="w-48 h-48 object-contain"
            />
          </div>
          <p className="mt-4 text-sm text-white/70">{t.instructions}</p>
          <div className="mt-4 flex items-center gap-2 text-[#ff4d4d]">
            <div className="w-2 h-2 rounded-full bg-[#ff4d4d] animate-pulse" />
            <span className="text-sm">{t.scanning}</span>
          </div>
          <button
            onClick={sendQRToEmail}
            disabled={emailSent}
            className="mt-4 px-4 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {emailSent ? t.emailSent : t.sendEmail}
          </button>
        </>
      )}

      {status === "expired" && (
        <div className="text-center">
          <div className="w-48 h-48 bg-white/5 rounded-xl flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-white/30" />
          </div>
          <p className="mt-4 text-white/50">{t.expired}</p>
          <button
            onClick={generateQR}
            className="mt-4 px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white rounded-lg transition-colors"
          >
            {t.regenerate}
          </button>
        </div>
      )}

      {status === "connected" && (
        <div className="text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <p className="mt-4 text-lg font-medium text-emerald-400">{t.connected}</p>
        </div>
      )}

      {status === "error" && (
        <div className="text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-red-400" />
          </div>
          <p className="mt-4 text-white/50">{t.error}</p>
          <p className="text-sm text-red-400">{error}</p>
          <button
            onClick={generateQR}
            className="mt-4 px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white rounded-lg transition-colors"
          >
            {t.retry}
          </button>
        </div>
      )}
    </div>
  );
};

// FAQ quick add component
const QuickFAQAdd = ({ accountId, onFAQsAdded, language = "en" }) => {
  const [faqs, setFaqs] = useState([
    { question: "", answer: "" },
    { question: "", answer: "" },
    { question: "", answer: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const texts = {
    en: {
      title: "Add Your First FAQs",
      subtitle: "Help your bot learn how to respond",
      question: "Question",
      answer: "Answer",
      addMore: "+ Add Another",
      save: "Save FAQs",
      skip: "Skip for now",
      placeholder_q: "e.g., What are your business hours?",
      placeholder_a: "e.g., We're open Monday-Friday, 9 AM to 6 PM.",
    },
    vi: {
      title: "Thêm câu hỏi thường gặp",
      subtitle: "Giúp bot học cách trả lời",
      question: "Câu hỏi",
      answer: "Trả lời",
      addMore: "+ Thêm câu hỏi",
      save: "Lưu câu hỏi",
      skip: "Bỏ qua",
      placeholder_q: "VD: Giờ làm việc của bạn là gì?",
      placeholder_a: "VD: Chúng tôi làm việc từ Thứ 2-6, 9h-18h.",
    },
  };
  const t = texts[language] || texts.en;

  const updateFAQ = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const addMoreFAQ = () => {
    setFaqs([...faqs, { question: "", answer: "" }]);
  };

  const handleSubmit = async () => {
    const validFaqs = faqs.filter((f) => f.question.trim() && f.answer.trim());
    if (validFaqs.length === 0) {
      if (onFAQsAdded) onFAQsAdded([]);
      return;
    }

    setIsSubmitting(true);
    try {
      // TODO: Call bulk create FAQs API
      // await bulkCreateAccountFAQs(accountId, validFaqs, true);
      if (onFAQsAdded) onFAQsAdded(validFaqs);
    } catch (err) {
      console.error("[Onboarding] FAQ save error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-lg font-medium text-white">{t.title}</h3>
        <p className="text-sm text-white/50">{t.subtitle}</p>
      </div>

      <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
        {faqs.map((faq, index) => (
          <div key={index} className="bg-white/5 rounded-lg p-4 space-y-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">
                {t.question} {index + 1}
              </label>
              <input
                type="text"
                value={faq.question}
                onChange={(e) => updateFAQ(index, "question", e.target.value)}
                placeholder={t.placeholder_q}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">{t.answer}</label>
              <textarea
                value={faq.answer}
                onChange={(e) => updateFAQ(index, "answer", e.target.value)}
                placeholder={t.placeholder_a}
                rows={2}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]/50 resize-none"
              />
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addMoreFAQ}
        className="w-full py-2 text-sm text-white/50 hover:text-white border border-dashed border-white/20 hover:border-white/40 rounded-lg transition-colors"
      >
        {t.addMore}
      </button>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => onFAQsAdded && onFAQsAdded([])}
          className="flex-1 py-2 text-white/50 hover:text-white rounded-lg transition-colors"
        >
          {t.skip}
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              {t.save}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

/**
 * Claw9Onboarding Component
 * Wizard-style onboarding for new Claw9 users
 */
const Claw9Onboarding = ({ onClose, onComplete, language = "en" }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(["signup"]);
  const [error, setError] = useState(null);

  // Data collected during onboarding
  const [accountId, setAccountId] = useState(null);
  const [botId, setBotId] = useState(null);
  const [botStatus, setBotStatus] = useState(null);

  const texts = {
    en: {
      title: "Welcome to Claw9!",
      subtitle: "Let's set up your AI chatbot",
      step: "Step",
      of: "of",
      next: "Continue",
      back: "Back",
      skip: "Skip",
      finish: "Go to Dashboard",
      creating_bot: "Creating your bot...",
      bot_ready: "Your bot is ready!",
      complete_title: "You're All Set!",
      complete_subtitle: "Your AI chatbot is ready to help your customers",
    },
    vi: {
      title: "Chào mừng đến Claw9!",
      subtitle: "Hãy thiết lập chatbot AI của bạn",
      step: "Bước",
      of: "của",
      next: "Tiếp tục",
      back: "Quay lại",
      skip: "Bỏ qua",
      finish: "Đến Dashboard",
      creating_bot: "Đang tạo bot...",
      bot_ready: "Bot đã sẵn sàng!",
      complete_title: "Hoàn tất!",
      complete_subtitle: "Chatbot AI của bạn đã sẵn sàng hỗ trợ khách hàng",
    },
  };
  const t = texts[language] || texts.en;

  // Check onboarding status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Fetch accounts and bots to determine current state
        const [accountsResult, agentsResult] = await Promise.all([
          fetchClaw9Accounts(),
          fetchBotAgents(),
        ]);

        const accounts = accountsResult?.accounts || [];
        const agents = agentsResult?.agents || [];

        const newCompletedSteps = ["signup"];

        if (accounts.length > 0) {
          setAccountId(accounts[0].account_id);
        }

        if (agents.length > 0) {
          const bot = agents[0];
          setBotId(bot.id || bot.name);
          setBotStatus(bot.status);
          newCompletedSteps.push("create_bot");

          // Check if Zalo is connected
          if (bot.status === "active" || bot.zalo_connected) {
            newCompletedSteps.push("connect_zalo");
          }

          // Check FAQs
          if (accounts.length > 0) {
            try {
              const faqsResult = await fetchAccountFAQs(accounts[0].account_id);
              const faqs = faqsResult?.data?.faqs || [];
              if (faqs.length >= 3) {
                newCompletedSteps.push("add_faqs");
              }
            } catch (e) {
              // Ignore FAQ fetch errors
            }
          }
        }

        setCompletedSteps(newCompletedSteps);

        // Determine current step
        const steps = ["signup", "create_bot", "connect_zalo", "add_faqs", "test_bot", "complete"];
        let currentIdx = 0;
        for (let i = 0; i < steps.length; i++) {
          if (!newCompletedSteps.includes(steps[i])) {
            currentIdx = i;
            break;
          }
        }
        setCurrentStepIndex(currentIdx);
      } catch (err) {
        console.error("[Onboarding] Status check error:", err);
        // Don't block rendering on error, just log it
      }
    };

    checkStatus();
  }, []);

  const currentStep = ONBOARDING_STEPS[currentStepIndex];
  const percentComplete = Math.round((completedSteps.length / ONBOARDING_STEPS.length) * 100);

  const handleStepComplete = (stepId) => {
    setCompletedSteps((prev) => [...new Set([...prev, stepId])]);
    if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleFinish = () => {
    if (onComplete) onComplete();
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#0d1424] rounded-2xl max-w-2xl w-full mx-4 overflow-hidden border border-white/10">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-white">{t.title}</h2>
              <p className="text-sm text-white/50">{t.subtitle}</p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-white/50 mb-2">
              <span>
                {t.step} {currentStepIndex + 1} {t.of} {ONBOARDING_STEPS.length}
              </span>
              <span>{percentComplete}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] transition-all duration-500"
                style={{ width: `${percentComplete}%` }}
              />
            </div>
          </div>

          {/* Step indicators */}
          <div className="mt-6 flex items-center justify-center overflow-x-auto pb-2">
            {ONBOARDING_STEPS.map((step, index) => (
              <StepIndicator
                key={step.id}
                step={step}
                index={index}
                currentStepIndex={currentStepIndex}
                completedSteps={completedSteps}
                language={language}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 min-h-[300px]">
          {/* Step: Connect Zalo */}
          {currentStep?.id === "connect_zalo" && (
            <ZaloQRConnect
              botId={botId}
              onConnected={() => handleStepComplete("connect_zalo")}
              language={language}
            />
          )}

          {/* Step: Add FAQs */}
          {currentStep?.id === "add_faqs" && (
            <QuickFAQAdd
              accountId={accountId}
              onFAQsAdded={() => handleStepComplete("add_faqs")}
              language={language}
            />
          )}

          {/* Step: Test Bot */}
          {currentStep?.id === "test_bot" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto">
                <TestTube className="w-10 h-10 text-purple-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-white">
                {language === "vi" ? "Thử nghiệm Bot" : "Test Your Bot"}
              </h3>
              <p className="mt-2 text-sm text-white/50 max-w-md mx-auto">
                {language === "vi"
                  ? "Gửi một tin nhắn đến bot của bạn để kiểm tra"
                  : "Send a test message to your bot to see it in action"}
              </p>
              <button
                onClick={() => handleStepComplete("test_bot")}
                className="mt-6 px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
              >
                {language === "vi" ? "Đánh dấu đã thử" : "Mark as Tested"}
              </button>
            </div>
          )}

          {/* Step: Complete */}
          {currentStep?.id === "complete" && (
            <div className="text-center py-8">
              <div className="w-24 h-24 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full flex items-center justify-center mx-auto">
                <Rocket className="w-12 h-12 text-emerald-400" />
              </div>
              <h3 className="mt-6 text-2xl font-bold text-white">{t.complete_title}</h3>
              <p className="mt-2 text-white/50">{t.complete_subtitle}</p>
              <button
                onClick={handleFinish}
                className="mt-8 px-8 py-3 bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] hover:from-[#ff3333] hover:to-[#ff5555] text-white rounded-lg font-medium transition-all flex items-center gap-2 mx-auto"
              >
                {t.finish}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Step: Create Bot (placeholder) */}
          {currentStep?.id === "create_bot" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-[#ff4d4d]/20 rounded-full flex items-center justify-center mx-auto">
                <Bot className="w-10 h-10 text-[#ff4d4d]" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-white">
                {language === "vi" ? "Tạo Bot của bạn" : "Create Your Bot"}
              </h3>
              <p className="mt-2 text-sm text-white/50">
                {botId
                  ? language === "vi"
                    ? `Bot "${botId}" đã được tạo!`
                    : `Bot "${botId}" is ready!`
                  : language === "vi"
                  ? "Đang thiết lập bot..."
                  : "Setting up your bot..."}
              </p>
              {botId && (
                <button
                  onClick={() => handleStepComplete("create_bot")}
                  className="mt-6 px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white rounded-lg transition-colors"
                >
                  {t.next}
                </button>
              )}
            </div>
          )}

          {/* Step: Signup (already complete) */}
          {currentStep?.id === "signup" && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-white">
                {language === "vi" ? "Tài khoản đã tạo!" : "Account Created!"}
              </h3>
              <p className="mt-2 text-sm text-white/50">
                {language === "vi"
                  ? "Hãy tiếp tục để thiết lập bot"
                  : "Let's continue to set up your bot"}
              </p>
              <button
                onClick={() => handleStepComplete("signup")}
                className="mt-6 px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white rounded-lg transition-colors"
              >
                {t.next}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {currentStep?.id !== "complete" && (
          <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStepIndex === 0}
              className="px-4 py-2 text-white/50 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              {t.back}
            </button>
            <div className="flex items-center gap-3">
              {currentStep?.id !== "connect_zalo" && currentStep?.id !== "add_faqs" && (
                <button
                  onClick={() => handleStepComplete(currentStep?.id)}
                  className="px-6 py-2 bg-[#ff4d4d] hover:bg-[#ff3333] text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {t.next}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Claw9Onboarding;
